import { useCallback, useEffect, useMemo, useState } from "react";
import useBudgetById from "../../../hooks/api/budget/useBudgetById";
import useProjectBudgetFormReducer from "../../../hooks/useProjectBudgetFormReducer";
import useEditLineitem from "../../../hooks/api/budget/useEditLineitem";
import useEditBudgetType from "../../../hooks/api/budget/useEditBudgetType ";
import { useProjectById } from "../../../hooks/projects";

const useActivateProjectForm = ({
  projectId,
  setActivateProjectModal,
  onActivateProjectCallback,
}) => {
  const { data: projectData } = useProjectById(projectId);
  const { editLineitem } = useEditLineitem();
  const { editBudgetType } = useEditBudgetType();
  const { budgetData, refetchBudget } = useBudgetById(
    projectData?.budget?.split("/")[1]
  );

  const [projectBudget, dispatchProjectBudget] = useProjectBudgetFormReducer();
  const [draftBudgetLineItems, setDraftBudgetLineItems] = useState([]);
  const [isActivatingProject, setIsActivatingProject] = useState(false);

  const budgetLastUpdated = budgetData?.budgetLastUpdated;
  const budgetType = budgetData?.budgetType;
  const budgetId = budgetData?.budget?.id;

  const isActivateFormValid = useMemo(() => {
    const isValidBudgetType = !!projectBudget?.budgetType?.length;

    const isAnyUnselected = draftBudgetLineItems?.some(
      (li) => !li.isHighRangeSelected && !li.isLowRangeSelected
    );

    return isValidBudgetType && !isAnyUnselected && !isActivatingProject;
  }, [
    draftBudgetLineItems,
    isActivatingProject,
    projectBudget?.budgetType?.length,
  ]);

  useEffect(() => {
    if (projectData?.reference) {
      dispatchProjectBudget({
        type: "reset",
        budget: {
          project: projectData?.reference,
          active: true,
        },
      });
    }
  }, [dispatchProjectBudget, projectData]);

  const onChangeBudgetType = (val) => {
    dispatchProjectBudget({
      type: "budgetType",
      budgetType: val.value,
    });
  };

  const onActivateProjectSubmit = useCallback(async () => {
    try {
      setIsActivatingProject(true);

      // reconcile budget
      Promise.all(
        draftBudgetLineItems.map(async (li) => {
          if (li?.isHighRangeSelected) {
            await editLineitem({
              id: li.id,
              editedItem: {
                ...li,
                costPerUnit: li?.highRange?.costPerUnit,
                quantity: li?.highRange?.quantity,
                adjustment: {
                  arithmeticUnit: li?.highRange?.arithmeticUnit,
                  adjustmentUnit: li?.highRange?.adjustmentUnit,
                  amount: li?.highRange?.amount,
                },
                isCostRange: false,
              },
              originalItem: li,
            });
          }
          return li;
        })
      );

      // set budget type
      await editBudgetType({
        budgetLastUpdated,
        budgetId,
        oldBudgetType: budgetType?.value,
        newBudgetType: projectBudget?.budgetType,
      });

      refetchBudget();

      // update project status
      await onActivateProjectCallback();
    } catch (err) {
      console.warn(err);
    } finally {
      setIsActivatingProject(false);
      setActivateProjectModal(false);
    }
  }, [
    budgetId,
    budgetLastUpdated,
    budgetType?.value,
    draftBudgetLineItems,
    editBudgetType,
    editLineitem,
    onActivateProjectCallback,
    projectBudget?.budgetType,
    refetchBudget,
    setActivateProjectModal,
  ]);

  const onReturnToBudgetView = () => {
    setActivateProjectModal(false);
  };

  return {
    projectBudget,
    projectData,
    isActivateFormValid,
    onChangeBudgetType,
    onActivateProjectSubmit,
    onReturnToBudgetView,
    setDraftBudgetLineItems,
  };
};

export default useActivateProjectForm;
